import type { Transformer } from "@/types/workspace";
import type { DataflowStore } from "@/types/stores/dataflow";
import { useDataProcessingStore } from "@/stores/data-processing";
import { useMapStore } from "@/stores/map";
import { logger } from "@/utils/debug";
import { createZustandIndexedDBStorage } from "@/services/storage/zustandIndexedDBStorage";
import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";

const initialDataflowState = {
  dataflows: {},
  activeDataflowId: null,
  openDataflowIds: [],
  savingStates: {},
  lastOpenedDataflowId: null,
  lastOpenedProjectId: null,
  triggerAutosave: undefined as (() => void) | undefined,
};

// Store the autosave callback globally
let autosaveCallback: (() => void) | undefined;

export const setAutosaveCallback = (callback: () => void) => {
  autosaveCallback = callback;
};

export const useDataflowStore = create<DataflowStore>()(
  devtools(
    persist(
      (set, get) => {
        return {
          ...initialDataflowState,

          clearAllDataflows: () =>
            set(
              () => ({
                dataflows: {},
                activeDataflowId: null,
                openDataflowIds: [],
                savingStates: {},
              }),
              false,
              "dataflow/clearAllDataflows"
            ),

          setActiveDataflow: (dataflowId) =>
            set(
              (state) => {
                const newState: any = { activeDataflowId: dataflowId };
                if (dataflowId && !state.openDataflowIds.includes(dataflowId)) {
                  newState.openDataflowIds = [
                    ...state.openDataflowIds,
                    dataflowId,
                  ];
                }
                return newState;
              },
              false,
              "dataflow/setActiveDataflow"
            ),

          addDataflow: (dataflowId, data) =>
            set(
              (state) => ({
                dataflows: {
                  ...state.dataflows,
                  [dataflowId]: data,
                },
                savingStates: {
                  ...state.savingStates,
                  [dataflowId]: false,
                },
              }),
              false,
              "dataflow/addDataflow"
            ),

          updateDataflow: (dataflowId, partial) =>
            set(
              (state) => ({
                dataflows: {
                  ...state.dataflows,
                  [dataflowId]: {
                    ...state.dataflows[dataflowId],
                    ...partial,
                  },
                },
              }),
              false,
              "dataflow/updateDataflow"
            ),

          removeDataflow: (dataflowId) =>
            set(
              (state) => {
                const { [dataflowId]: removedDataflow, ...remainingDataflows } =
                  state.dataflows;
                const {
                  [dataflowId]: removedSaving,
                  ...remainingSavingStates
                } = state.savingStates;

                return {
                  dataflows: remainingDataflows,
                  savingStates: remainingSavingStates,
                  openDataflowIds: state.openDataflowIds.filter(
                    (id) => id !== dataflowId
                  ),
                  activeDataflowId:
                    state.activeDataflowId === dataflowId
                      ? null
                      : state.activeDataflowId,
                };
              },
              false,
              "dataflow/removeDataflow"
            ),

          setDataflowName: (dataflowId, name) =>
            set(
              (state) => ({
                dataflows: {
                  ...state.dataflows,
                  [dataflowId]: {
                    ...state.dataflows[dataflowId],
                    dataflow_name: name,
                  },
                },
              }),
              false,
              "dataflow/setDataflowName"
            ),

          setDataflowDescription: (dataflowId, description) =>
            set(
              (state) => ({
                dataflows: {
                  ...state.dataflows,
                  [dataflowId]: {
                    ...state.dataflows[dataflowId],
                    dataflow_description: description,
                  },
                },
              }),
              false,
              "dataflow/setDataflowDescription"
            ),

          addTransformer: (dataflowId, transformer: Transformer) => {
            set(
              (state) => {
                const dataflow = state.dataflows[dataflowId];
                if (!dataflow) return state;

                const updatedDataflow = {
                  ...dataflow,
                  flow_definition_json: [
                    ...(dataflow.flow_definition_json || []),
                    transformer,
                  ],
                };

                // Trigger data processing execution for user_file dataflows
                const { executeDataflow } = useDataProcessingStore.getState();
                const hasUserFile =
                  updatedDataflow.flow_definition_json?.some(
                    (t) => t.type === "user_file"
                  );
                if (hasUserFile) {
                  executeDataflow(dataflowId, updatedDataflow).catch(
                    (error) => logger.error(error)
                  );
                }

                return {
                  dataflows: {
                    ...state.dataflows,
                    [dataflowId]: updatedDataflow,
                  },
                };
              },
              false,
              "dataflow/addTransformer"
            );
            // Trigger autosave after adding transformer
            autosaveCallback?.();
          },

          updateTransformer: (dataflowId, index, transformer: Transformer) => {
            set(
              (state) => {
                const dataflow = state.dataflows[dataflowId];
                if (!dataflow || !dataflow.flow_definition_json) return state;

                const oldTransformer = dataflow.flow_definition_json[index];
                const updatedTransformers = [...dataflow.flow_definition_json];
                updatedTransformers[index] = transformer;

                const updatedDataflow = {
                  ...dataflow,
                  flow_definition_json: updatedTransformers,
                };

                // Check if the input transformer changed (user_file or data_input)
                const isInputTransformer =
                  transformer.type === "user_file" ||
                  transformer.type === "data_input";
                const wasInputTransformer =
                  oldTransformer?.type === "user_file" ||
                  oldTransformer?.type === "data_input";

                // If input transformer changed, clear processed data to force fresh fetch
                if (isInputTransformer && wasInputTransformer) {
                  const oldInputId =
                    oldTransformer.type === "user_file"
                      ? (oldTransformer as any).file_id
                      : (oldTransformer as any).db_table_id;
                  const newInputId =
                    transformer.type === "user_file"
                      ? (transformer as any).file_id
                      : (transformer as any).db_table_id;

                  // If the input ID changed, clear processed data
                  if (oldInputId !== newInputId) {
                    const { clearProcessedData } =
                      useDataProcessingStore.getState();
                    clearProcessedData(dataflowId);
                  }
                }

                // Trigger data processing execution for user_file and data_input dataflows
                const { executeDataflow } = useDataProcessingStore.getState();
                const hasDataInput =
                  updatedDataflow.flow_definition_json?.some(
                    (t) => t.type === "user_file" || t.type === "data_input"
                  );
                if (hasDataInput) {
                  executeDataflow(dataflowId, updatedDataflow).catch(
                    (error) => logger.error(error)
                  );
                }

                return {
                  dataflows: {
                    ...state.dataflows,
                    [dataflowId]: updatedDataflow,
                  },
                };
              },
              false,
              "dataflow/updateTransformer"
            );
            // Trigger autosave after updating transformer
            autosaveCallback?.();
          },

          removeTransformer: (dataflowId, index) => {
            set(
              (state) => {
                const dataflow = state.dataflows[dataflowId];
                if (!dataflow || !dataflow.flow_definition_json) return state;

                const transformerToRemove =
                  dataflow.flow_definition_json[index];
                const updatedTransformers = [...dataflow.flow_definition_json];
                updatedTransformers.splice(index, 1);

                const updatedDataflow = {
                  ...dataflow,
                  flow_definition_json: updatedTransformers,
                };

                // Check if we're removing the input transformer (data_input or user_file)
                const isRemovingInputTransformer =
                  transformerToRemove?.type === "data_input" ||
                  transformerToRemove?.type === "user_file";

                // If removing input transformer, clear processed data and map layers
                if (isRemovingInputTransformer) {
                  // Clear processed data
                  const { clearProcessedData } =
                    useDataProcessingStore.getState();
                  clearProcessedData(dataflowId);

                  // Remove map layer
                  const { removeGeoJSONLayer } = useMapStore.getState();
                  const layerId = `processed-${dataflowId}`;
                  removeGeoJSONLayer(layerId);
                } else {
                  // Otherwise, trigger data processing execution for user_file dataflows
                  const { executeDataflow } = useDataProcessingStore.getState();
                  const hasUserFile =
                    updatedDataflow.flow_definition_json?.some(
                      (t) => t.type === "user_file"
                    );
                  if (hasUserFile) {
                    executeDataflow(dataflowId, updatedDataflow).catch(
                      (error) => logger.error(error)
                    );
                  }
                }

                return {
                  dataflows: {
                    ...state.dataflows,
                    [dataflowId]: updatedDataflow,
                  },
                };
              },
              false,
              "dataflow/removeTransformer"
            );
            // Trigger autosave after removing transformer
            autosaveCallback?.();
          },

          reorderTransformers: (dataflowId, transformers: Transformer[]) => {
            set(
              (state) => {
                const dataflow = state.dataflows[dataflowId];
                if (!dataflow) return state;

                return {
                  dataflows: {
                    ...state.dataflows,
                    [dataflowId]: {
                      ...dataflow,
                      flow_definition_json: transformers,
                    },
                  },
                };
              },
              false,
              "dataflow/reorderTransformers"
            );
            // Trigger autosave after reordering transformers
            autosaveCallback?.();
          },

          setSaving: (dataflowId, isSaving) =>
            set(
              (state) => ({
                savingStates: {
                  ...state.savingStates,
                  [dataflowId]: isSaving,
                },
              }),
              false,
              "dataflow/setSaving"
            ),

          getDataflow: (dataflowId) => {
            const state = get();
            return state.dataflows[dataflowId];
          },

          isDataflowOpen: (dataflowId) => {
            const state = get();
            return state.openDataflowIds.includes(dataflowId);
          },

          setLastOpenedDataflow: (dataflowId) =>
            set(
              { lastOpenedDataflowId: dataflowId },
              false,
              "dataflow/setLastOpenedDataflow"
            ),

          setLastOpenedProject: (projectId) =>
            set(
              { lastOpenedProjectId: projectId },
              false,
              "dataflow/setLastOpenedProject"
            ),
        };
      },
      {
        name: "dataflow-store",
        storage: createJSONStorage(createZustandIndexedDBStorage("workspaces")),
        partialize: (state) => ({
          dataflows: state.dataflows,
          openDataflowIds: state.openDataflowIds,
          lastOpenedDataflowId: state.lastOpenedDataflowId,
          lastOpenedProjectId: state.lastOpenedProjectId,
        }),
        migrate: (persistedState: any, version: number) => {
          if (version === 0) {
            return {
              dataflows: {},
              openDataflowIds: [],
            };
          }
          return persistedState;
        },
        version: 1,
      }
    ),
    {
      name: "dataflow-store",
      serialize: {
        options: {
          date: false,
          error: false,
          undefined: false,
          map: false,
          set: false,
          function: false,
        },
        refs: true,
        maxDepth: 1,
      },
    }
  )
);

export const useActiveDataflow = () =>
  useDataflowStore((state) => {
    const { activeDataflowId, dataflows } = state;
    return activeDataflowId ? dataflows[activeDataflowId] : undefined;
  });

export const useDataflow = (dataflowId: string) =>
  useDataflowStore((state) => state.dataflows[dataflowId]);

export const useOpenDataflows = () =>
  useDataflowStore((state) => state.openDataflowIds);

export const useIsSaving = (dataflowId: string) =>
  useDataflowStore((state) => state.savingStates[dataflowId] || false);

export const useActiveDataflowId = () =>
  useDataflowStore((state) => state.activeDataflowId);

export const clearDataflowStore = () => {
  useDataflowStore.getState().clearAllDataflows();
};

if (typeof window !== "undefined") {
  (window as any).clearDataflowStore = clearDataflowStore;
}

export const useDataflowActions = () => {
  const setActiveDataflow = useDataflowStore(
    (state) => state.setActiveDataflow
  );
  const addDataflow = useDataflowStore((state) => state.addDataflow);
  const updateDataflow = useDataflowStore((state) => state.updateDataflow);
  const removeDataflow = useDataflowStore((state) => state.removeDataflow);
  const setDataflowName = useDataflowStore((state) => state.setDataflowName);
  const setDataflowDescription = useDataflowStore(
    (state) => state.setDataflowDescription
  );
  const addTransformer = useDataflowStore((state) => state.addTransformer);
  const updateTransformer = useDataflowStore(
    (state) => state.updateTransformer
  );
  const removeTransformer = useDataflowStore(
    (state) => state.removeTransformer
  );
  const reorderTransformers = useDataflowStore(
    (state) => state.reorderTransformers
  );
  const setSaving = useDataflowStore((state) => state.setSaving);

  return {
    setActiveDataflow,
    addDataflow,
    updateDataflow,
    removeDataflow,
    setDataflowName,
    setDataflowDescription,
    addTransformer,
    updateTransformer,
    removeTransformer,
    reorderTransformers,
    setSaving,
  };
};

export const useDataflowData = () => useActiveDataflow();
export const useIsSavingActive = () =>
  useDataflowStore((state) => {
    const { activeDataflowId, savingStates } = state;
    return activeDataflowId ? savingStates[activeDataflowId] || false : false;
  });

